import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import { getCmsRestaurantsEndpointIdentifier } from 'apis/restaurants/use-get-cms-restaurants-api';
import type { AxiosError } from 'axios';
import { useQueryClient } from 'react-query';
import type { UseMutationOptions, UseMutationResult } from 'react-query';

const endpoint = 'ums/v1/restaurants';
export const createRestaurantEndpointIdentifier = endpoint;

interface BranchBody {
  nameEn: string;
  nameAr?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  fullLine?: string;
}

interface AdminBody {
  fullName: string;
  phoneNumber: string;
  email?: string;
}

interface BodyType {
  nameEn: string;
  nameAr?: string;
  countryCode: string;
  imageUrl?: string;
  branch: BranchBody;
  admin: AdminBody;
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  body: BodyType;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useCreateRestaurantApi(options?: Options): ReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();

  const mutationFn = async ({ body }: TVariables) => {
    try {
      const { data } = await http.post(endpoint, body);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    ...options,
    mutationKey: [endpoint],
    onSuccess: () => {
      queryClient.invalidateQueries([getCmsRestaurantsEndpointIdentifier]);
      toast({
        description: 'Restaurant has been created successfully',
        status: 'success',
      });
    },
  });
}
